import { useCallback, useState, type SetStateAction } from "react";

/**
 * State that starts over when `key` changes, and only then. It replaces the pattern
 * `useState(init)` + `useEffect(() => set(init), [key])`, which also ran once after the FIRST render: a tap made between the
 * first render and that effect was overwritten by the effect (measured: the allergen "confirm a dish" test lost its
 * sesame tap in 3 of ~48 loaded runs and waited 20 s for a state that never came; a person's first tap could be lost the
 * same way). Here the reset happens during render, the way React documents for state derived from a changing prop, so
 * nothing runs after mount.
 */
export function useKeyedState<T>(key: unknown, init: () => T): [T, (next: SetStateAction<T>) => void] {
  const [held, setHeld] = useState(() => ({ key, value: init() }));
  let cur = held;
  if (!Object.is(held.key, key)) { cur = { key, value: init() }; setHeld(cur); }
  const set = useCallback((next: SetStateAction<T>) => setHeld((h) => ({ key: h.key, value: typeof next === "function" ? (next as (v: T) => T)(h.value) : next })), []);
  return [cur.value, set];
}
